import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Card,
  Typography,
  Space,
  Button,
  Form,
  InputNumber,
  Row,
  Col,
  Tag,
  Tabs,
  Spin,
  Empty,
  message,
  Divider,
  Select,
  Statistic,
  Alert,
} from 'antd';
import {
  SettingOutlined,
  WalletOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import apiService from '../services/ApiService';

const { Content, Header } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_LABELS = {
  pending: { text: 'Ожидает', color: 'orange', icon: <ClockCircleOutlined /> },
  approved: { text: 'Одобрена', color: 'green', icon: <CheckCircleOutlined /> },
  rejected: { text: 'Отклонена', color: 'red', icon: <CloseCircleOutlined /> },
};

const AdminReferralPage = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  const [settingsForm] = Form.useForm();

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);

  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  // Auth guard
  useEffect(() => {
    if (!user) { navigate('/'); return; }
    if (!user.is_admin) { navigate('/'); return; }
  }, [user, navigate]);

  // Back button
  useEffect(() => {
    const bb = window.Telegram?.WebApp?.BackButton;
    if (bb) {
      bb.show();
      const handler = () => navigate('/admin');
      bb.onClick(handler);
      return () => { try { bb.offClick(handler); bb.hide(); } catch (_) {} };
    }
  }, [navigate]);

  // Load settings
  useEffect(() => {
    if (!user?.is_admin) return;
    apiService.getAdminReferralSettings(user.user_id)
      .then((data) => {
        setSettings(data);
        settingsForm.setFieldsValue({
          level1_percent: data.level1_percent,
          level2_percent: data.level2_percent,
          min_withdrawal: data.min_withdrawal,
        });
      })
      .catch(() => message.error('Ошибка загрузки настроек'))
      .finally(() => setSettingsLoading(false));
  }, [user, settingsForm]);

  // Load withdrawals
  const loadWithdrawals = useCallback(async () => {
    if (!user?.is_admin) return;
    setWithdrawalsLoading(true);
    try {
      const data = await apiService.getAdminWithdrawals(user.user_id, statusFilter);
      setWithdrawals(data);
      setStats({
        total: data.length,
        pending: data.filter((w) => w.status === 'pending').length,
        approved: data.filter((w) => w.status === 'approved').length,
        rejected: data.filter((w) => w.status === 'rejected').length,
      });
    } catch (e) {
      setWithdrawals([]);
    } finally {
      setWithdrawalsLoading(false);
    }
  }, [user, statusFilter]);

  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);

  const onSaveSettings = async (values) => {
    setSettingsSaving(true);
    try {
      const updated = await apiService.updateAdminReferralSettings(user.user_id, values);
      setSettings(updated);
      message.success('Настройки сохранены');
    } catch (e) {
      message.error('Ошибка сохранения настроек');
    } finally {
      setSettingsSaving(false);
    }
  };

  const settingsPanel = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {settingsLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <Form form={settingsForm} layout="vertical" onFinish={onSaveSettings}>
          <Alert
            message="Изменения вступят в силу для всех новых платежей"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="level1_percent"
                label="% за прямых клиентов (Партнёрка 1)"
                rules={[{ required: true, message: 'Обязательное поле' }]}
                tooltip="Процент от суммы платежа, начисляемый партнёру за его прямого клиента"
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.5}
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="level2_percent"
                label="% за партнёров (Партнёрка 2)"
                rules={[{ required: true, message: 'Обязательное поле' }]}
                tooltip="Процент от суммы платежа клиентов нижестоящего партнёра"
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.5}
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="min_withdrawal"
            label="Минимальная сумма вывода (₽)"
            rules={[{ required: true, message: 'Обязательное поле' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} addonAfter="₽" />
          </Form.Item>

          {settings && (
            <Card size="small" style={{ background: 'var(--ant-color-bg-layout)', marginBottom: 16 }}>
              <Row gutter={12}>
                <Col span={8}>
                  <Statistic
                    title="Партнёрка 1"
                    value={settings.level1_percent}
                    suffix="%"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Партнёрка 2"
                    value={settings.level2_percent}
                    suffix="%"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Мин. вывод"
                    value={settings.min_withdrawal}
                    suffix="₽"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          <Button type="primary" htmlType="submit" block loading={settingsSaving}>
            Сохранить настройки
          </Button>
        </Form>
      )}
    </Space>
  );

  const withdrawalsPanel = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Статистика */}
      <Row gutter={12}>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="Всего" value={stats.total} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="Ожидают" value={stats.pending} valueStyle={{ fontSize: 18, color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="Одобрены" value={stats.approved} valueStyle={{ fontSize: 18, color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" style={{ textAlign: 'center' }}>
            <Statistic title="Откл." value={stats.rejected} valueStyle={{ fontSize: 18, color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      {/* Фильтр */}
      <Row gutter={8} align="middle">
        <Col flex="auto">
          <Select
            style={{ width: '100%' }}
            placeholder="Фильтр по статусу"
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <Option value="pending">Ожидают</Option>
            <Option value="approved">Одобренные</Option>
            <Option value="rejected">Отклонённые</Option>
          </Select>
        </Col>
        <Col>
          <Button onClick={loadWithdrawals}>Обновить</Button>
        </Col>
      </Row>

      {/* Список заявок */}
      {withdrawalsLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : withdrawals.length === 0 ? (
        <Empty description="Заявок нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {withdrawals.map((w) => (
            <Card
              key={w.id}
              size="small"
              style={{ background: 'var(--ant-color-bg-layout)' }}
            >
              <Row justify="space-between" align="top">
                <Col flex="auto">
                  <Space direction="vertical" size={2}>
                    <Row gutter={8} align="middle">
                      <Col>
                        <Text strong>#{w.id} — {w.amount} ₽</Text>
                      </Col>
                      <Col>
                        <Tag color={STATUS_LABELS[w.status]?.color}>
                          {STATUS_LABELS[w.status]?.text}
                        </Tag>
                      </Col>
                    </Row>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Пользователь: <Text code style={{ fontSize: 12 }}>{w.user_id}</Text>
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {w.method === 'sbp'
                        ? `📱 СБП · ${w.phone} · ${w.bank}`
                        : `💳 Карта · ${w.card_number}`}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Создана: {new Date(w.created_at).toLocaleString('ru-RU')}
                    </Text>
                    {w.processed_at && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Обработана: {new Date(w.processed_at).toLocaleString('ru-RU')}
                      </Text>
                    )}
                    {w.rejection_reason && (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        Причина: {w.rejection_reason}
                      </Text>
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      )}
    </Space>
  );

  const tabItems = [
    {
      key: 'settings',
      label: <><SettingOutlined /> Настройки</>,
      children: settingsPanel,
    },
    {
      key: 'withdrawals',
      label: <><WalletOutlined /> Заявки на вывод</>,
      children: withdrawalsPanel,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: 'transparent', padding: '0 16px', borderBottom: '1px solid var(--ant-color-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Партнёрская программа
          </Title>
        </div>
      </Header>

      <Content style={{ padding: '16px' }}>
        <Tabs items={tabItems} defaultActiveKey="settings" />
      </Content>
    </Layout>
  );
};

export default AdminReferralPage;
